import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { posTenbisService, type TenbisWebsiteModeRequest, type TenbisWebsiteModeView } from '../../../../services/pos-tenbis/pos-tenbis.service';

type Params = {
  tenantId: string;
  employeeSession: string | null;
  opened: boolean;
};

// Fetches the last-sent mode once per open (no polling); one in-flight change at a time.
export function useTenbisWebsiteModeController({ tenantId, employeeSession, opened }: Params) {
  const [state, setState] = useState<TenbisWebsiteModeView | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const saveLockRef = useRef(false);

  useEffect(() => {
    if (!opened || !employeeSession) {
      setState(null);
      setError(null);
      return;
    }
    let active = true;
    setIsLoading(true);
    setError(null);
    posTenbisService
      .getWebsiteMode(tenantId, employeeSession)
      .then((view) => {
        if (active) setState(view);
      })
      .catch((loadError: unknown) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'קריאת סטטוס המסעדה בתן ביס נכשלה');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [opened, tenantId, employeeSession]);

  const setWebsiteMode = useCallback(async (payload: TenbisWebsiteModeRequest): Promise<boolean> => {
    if (!employeeSession || saveLockRef.current) {
      return false;
    }
    saveLockRef.current = true;
    setIsSaving(true);
    try {
      const view = await posTenbisService.setWebsiteMode(tenantId, employeeSession, payload);
      setState(view);
      notifications.show({
        color: 'green',
        title: 'תן ביס',
        message: view.mode === 'temp_closed' ? 'המסעדה נסגרה זמנית בתן ביס' : 'המסעדה פתוחה בתן ביס',
        autoClose: 3000,
      });
      return true;
    } catch (saveError: unknown) {
      notifications.show({
        color: 'red',
        title: 'תן ביס',
        message: saveError instanceof Error ? saveError.message : 'עדכון סטטוס המסעדה בתן ביס נכשל',
        autoClose: 4000,
      });
      return false;
    } finally {
      saveLockRef.current = false;
      setIsSaving(false);
    }
  }, [employeeSession, tenantId]);

  return { state, isLoading, isSaving, error, setWebsiteMode };
}
